/**
 * Firestore Repo Layer
 * --------------------
 * Components/hooks should NOT import firebase/firestore directly.
 * All Firestore reads/writes live in small "repo" modules in /data so we can
 * later swap the implementation (Web SDK today, Native SDK later) without
 * touching UI components or hooks.
 */

import { addDoc, collection, serverTimestamp, getDocs, doc, getDoc, setDoc, deleteDoc,query, where, updateDoc } from "firebase/firestore";
import { db } from "../firebase";

export async function getFlashcardStatusCounts({ uid, language }) {
    if (!uid) throw new Error("getFlashcardStatusCounts: uid is required");
    if (!language) throw new Error("getFlashcardStatusCounts: language is required");
  
    const ref = collection(db, "users", uid, `flashcards_${language}`);
    const snap = await getDocs(ref);
  
    const counts = { total: snap.size, new: 0, learning: 0, known: 0 };
  
    snap.forEach((docSnap) => {
      const status = docSnap.data()?.status;
      if (status === "new") counts.new++;
      else if (status === "learning") counts.learning++;
      else if (status === "known") counts.known++;
    });
  
    return counts;
}

export async function submitFlashcardReport({ uid, flashcard, reason, details }) {
  if (!uid) throw new Error("submitFlashcardReport: uid is required");
  if (!flashcard?.id) throw new Error("submitFlashcardReport: flashcard.id is required");
  if (!reason) throw new Error("submitFlashcardReport: reason is required");

  await addDoc(collection(db, "reports"), {
    userId: uid,
    flashcardId: flashcard.id,
    word: flashcard.word ?? null,
    reason,
    details: details ?? "",
    timestamp: serverTimestamp(),
  });
}

export async function getDictionaryEntry({ lemma, pos }) {
  if (!lemma) throw new Error("getDictionaryEntry: lemma is required");
  if (!pos) throw new Error("getDictionaryEntry: pos is required");

  const key = `${lemma}_${pos}`;
  const ref = doc(db, "deupor", key);
  const snap = await getDoc(ref);
  return snap.exists() ? snap.data() : null;
}

export async function listSavedFlashcardIds({ uid, language }) {
  if (!uid) throw new Error("listSavedFlashcardIds: uid is required");
  if (!language) throw new Error("listSavedFlashcardIds: language is required");

  const ref = collection(db, "users", uid, `flashcards_${language}`);
  const snap = await getDocs(ref);

  // you currently use doc.id as the word key
  return snap.docs.map((d) => d.id.toLowerCase());
}

export async function toggleFlashcardForUser({
  uid,
  language,
  wordKey,      // lowercased id
  wordData,     // full word object
  storyId,
  sentence,
}) {
  if (!uid) throw new Error("toggleFlashcardForUser: uid is required");
  if (!language) throw new Error("toggleFlashcardForUser: language is required");
  if (!wordKey) throw new Error("toggleFlashcardForUser: wordKey is required");

  const ref = doc(db, "users", uid, `flashcards_${language}`, wordKey);
  const isDelete = !wordData; // not used here, but keeping pattern simple

  // We won’t “check existence” here because your UI already knows isSaved.
  // This repo assumes the caller decided what to do.
}

export async function saveFlashcard({
  uid,
  language,
  wordKey,
  wordData,
  storyId,
  sentence,
  sentenceTranslation,
}) {
  const ref = doc(db, "users", uid, `flashcards_${language}`, wordKey);
  await setDoc(ref, {
    ...wordData,
    status: "new",
    difficulty: "hard",
    timesReviewed: 0,
    lastReviewed: serverTimestamp(),
    storyId: storyId ?? null,
    sentence: sentence ?? null,
    sentenceTranslation: sentenceTranslation ?? null,
  });
}

export async function removeFlashcard({ uid, language, wordKey }) {
  const ref = doc(db, "users", uid, `flashcards_${language}`, wordKey);
  await deleteDoc(ref);
}

/**
 * List flashcards for a user, optionally filtered by status.
 * Path: users/{uid}/flashcards_{language}
 */
export async function listFlashcards({ uid, language, status = "all" }) {
  if (!uid) throw new Error("listFlashcards: uid is required");
  if (!language) throw new Error("listFlashcards: language is required");

  const baseRef = collection(db, "users", uid, `flashcards_${language}`);
  const q = status === "all" ? baseRef : query(baseRef, where("status", "==", status));

  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Update flashcard review status/scheduling fields.
 * Keeps Firestore writes out of UI.
 */
export async function updateFlashcardStatus({
  uid,
  language,
  cardId,
  status,
  difficulty,
  nextReview,
  lastReviewed,
  timesReviewed,
}) {
  if (!uid) throw new Error("updateFlashcardReview: uid is required");
  if (!language) throw new Error("updateFlashcardReview: language is required");
  if (!cardId) throw new Error("updateFlashcardReview: cardId is required");

  const ref = doc(db, "users", uid, `flashcards_${language}`, cardId);

  await updateDoc(ref, {
    status,
    difficulty,
    lastReviewed,     // can be Date; Firestore will store as timestamp
    timesReviewed,
    nextReview,       // whatever scheduleNext returns (Date or Timestamp)
  });
}

